import * as React from 'react'
import { useCallback } from 'react'

import { IUploadLog } from '~/client/graph'
import { getAttachmentFileType } from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import FileType from '~/client/src/shared/enums/FileType'

import './AttachmentHoverPreview.scss'

interface IProps {
  uploadLog: IUploadLog
  isVisible: boolean
  position: { x: number; y: number }
}

const PREVIEW_OFFSET_X = 10
const PREVIEW_OFFSET_Y = -140
const FILE_SIZE_UNITS = ['Bytes', 'KB', 'MB', 'GB']
const BYTES_PER_UNIT = 1024
const DOT_SEPARATOR = '.'
const SLASH_SEPARATOR = '/'
interface IDocumentHelperInfo {
  label: string
  colorClass: string
  backgroundClass: string
  abbreviation: string
}
const DOCUMENT_HELPER_INFO: Partial<Record<FileType, IDocumentHelperInfo>> = {
  [FileType.Word]: {
    label: 'Microsoft Word',
    colorClass: 'word-color',
    backgroundClass: 'word-bg',
    abbreviation: 'DOC',
  },
  [FileType.Excel]: {
    label: 'Microsoft Excel',
    colorClass: 'excel-color',
    backgroundClass: 'excel-bg',
    abbreviation: 'XLS',
  },
  [FileType.PowerPoint]: {
    label: 'Microsoft PowerPoint',
    colorClass: 'powerpoint-color',
    backgroundClass: 'powerpoint-bg',
    abbreviation: 'PPT',
  },
}

const DEFAULT_DOCUMENT_HELPER_INFO: IDocumentHelperInfo = {
  label: 'Document',
  colorClass: 'default-color',
  backgroundClass: 'default-bg',
  abbreviation: 'DOC',
}

const AttachmentHoverPreview: React.FC<IProps> = ({
  uploadLog,
  isVisible,
  position,
}) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'

    const i = Math.floor(Math.log(bytes) / Math.log(BYTES_PER_UNIT))
    return (
      parseFloat((bytes / Math.pow(BYTES_PER_UNIT, i)).toFixed(2)) +
      ' ' +
      FILE_SIZE_UNITS[i]
    )
  }

  const getDisplayFileName = (uploadLog: IUploadLog): string => {
    if (uploadLog.fileUrl) {
      const urlParts = uploadLog.fileUrl.split(SLASH_SEPARATOR)
      const fileName = urlParts[urlParts.length - 1]
      if (fileName && fileName.includes(DOT_SEPARATOR)) {
        return decodeURIComponent(fileName)
      }
    }

    return `file${DOT_SEPARATOR}${uploadLog.fileType}`
  }

  const getFileSize = (uploadLog: IUploadLog): string => {
    if (uploadLog.metadata) {
      const sizeMetadata = uploadLog.metadata.find(
        meta =>
          meta?.key?.toLowerCase().includes('size') ||
          meta?.key?.toLowerCase().includes('length'),
      )
      if (sizeMetadata?.value) {
        const bytes = parseInt(sizeMetadata.value, 10)
        if (!isNaN(bytes)) {
          return formatFileSize(bytes)
        }
      }
    }
    return null
  }

  const getDocumentColorClass = (fileType: FileType): string => {
    return (
      DOCUMENT_HELPER_INFO[fileType]?.colorClass ||
      DEFAULT_DOCUMENT_HELPER_INFO.colorClass
    )
  }

  const getDocumentBackgroundClass = (fileType: FileType): string => {
    return (
      DOCUMENT_HELPER_INFO[fileType]?.backgroundClass ||
      DEFAULT_DOCUMENT_HELPER_INFO.backgroundClass
    )
  }

  const getDocumentAbbreviation = (fileType: FileType): string => {
    return (
      DOCUMENT_HELPER_INFO[fileType]?.abbreviation ||
      DEFAULT_DOCUMENT_HELPER_INFO.abbreviation
    )
  }

  const handleImageError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>): void => {
      const img = event.currentTarget
      img.className = 'attachment-image-error-fallback'

      const parent = img.parentElement
      if (parent) {
        parent.innerHTML = `
        <div class="col y-center x-center pa16 text-center">
          <div class="text">Image preview unavailable</div>
          <div class="text small text-grey mt8">Click to view full image</div>
        </div>
      `
      }
    },
    [],
  )

  const renderDocumentIcon = (fileType: FileType): JSX.Element => {
    return (
      <div
        className={`attachment-document-icon ${getDocumentBackgroundClass(
          fileType,
        )}`}
      >
        <div className="attachment-document-icon-inner">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`attachment-document-line line-${i}`} />
          ))}
          <div
            className={`attachment-document-type-badge ${getDocumentColorClass(
              fileType,
            )}`}
          >
            {getDocumentAbbreviation(fileType)}
          </div>
        </div>
      </div>
    )
  }

  const renderImagePreview = (fileUrl: string): JSX.Element => {
    return (
      <div className="full-height row y-center x-center">
        <img
          src={fileUrl}
          alt="Preview"
          className="attachment-preview-image"
          onError={handleImageError}
        />
      </div>
    )
  }

  const renderPdfPreview = (fileUrl: string): JSX.Element => {
    return (
      <div className="full-height col y-center x-center">
        <iframe
          src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
          className="attachment-preview-pdf-iframe"
          title="PDF Preview"
        />
      </div>
    )
  }

  const renderVideoPreview = (fileUrl: string): JSX.Element => {
    return (
      <div className="full-height row y-center x-center">
        <video
          src={fileUrl}
          className="attachment-preview-video"
          controls={false}
          muted
          preload="metadata"
        />
      </div>
    )
  }

  const renderDocumentPreview = (
    fileType: FileType,
    uploadLog: IUploadLog,
  ): JSX.Element => {
    const fileName = getDisplayFileName(uploadLog)
    const fileSize = getFileSize(uploadLog)

    return (
      <div className="full-height col y-center x-center pa16 text-center full-width">
        <div className="mb16">{renderDocumentIcon(fileType)}</div>
        <div className="full-width">
          <div className="text large mx5 text-ellipsis center">{fileName}</div>
          {fileSize && <div className="text small text-grey">{fileSize}</div>}
        </div>
      </div>
    )
  }

  const renderGenericPreview = (uploadLog: IUploadLog): JSX.Element => {
    const fileName = getDisplayFileName(uploadLog)
    const fileSize = getFileSize(uploadLog)
    const fileType = getAttachmentFileType(uploadLog.fileType || '', '/', true)

    return (
      <div className="full-height col y-center x-center pa16 text-center">
        <div className="mb16">
          <div className="text large mb8">{fileName}</div>
          <div className="text small text-grey mb4">
            Type:{' '}
            {typeof fileType === 'string' ? fileType.toUpperCase() : 'Unknown'}
          </div>
          {fileSize && (
            <div className="text small text-grey">Size: {fileSize}</div>
          )}
        </div>
        <div className="text small text-grey">
          Preview not available for this file type
        </div>
      </div>
    )
  }

  const renderPreviewContent = (
    fileType: FileType | string,
    uploadLog: IUploadLog,
  ): JSX.Element => {
    const { fileUrl } = uploadLog

    switch (fileType) {
      case FileType.Image:
        return renderImagePreview(fileUrl)
      case FileType.Pdf:
        return renderPdfPreview(fileUrl)
      case FileType.Video:
        return renderVideoPreview(fileUrl)
      case FileType.Word:
      case FileType.Excel:
      case FileType.PowerPoint:
        return renderDocumentPreview(fileType, uploadLog)
      default:
        return renderGenericPreview(uploadLog)
    }
  }

  if (!isVisible || !uploadLog?.fileUrl) {
    return null
  }

  const fileType = getAttachmentFileType(uploadLog.fileType || '', '/', false)
  const style: React.CSSProperties = {
    left: position.x + PREVIEW_OFFSET_X,
    top: position.y + PREVIEW_OFFSET_Y,
  }

  return (
    <div style={style} className="attachment-hover-preview">
      {renderPreviewContent(fileType, uploadLog)}
    </div>
  )
}

export default AttachmentHoverPreview
